import { format, getMonth, getYear, UTCDate } from '@/utils/dates';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, DayPickerProps, MonthCaptionProps, useDayPicker, Weekdays } from 'react-day-picker';
import { enIE } from 'react-day-picker/locale';
// https://daypicker.dev/docs/styling#default-style
import 'react-day-picker/style.css';
import { Button } from '../button/button';
import { Inline } from '../inline/inline';

export const Calendar = ({ className, classNames, showOutsideDays = true, ...props }: DayPickerProps) => {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      numberOfMonths={2}
      className={className}
      // Always displays current month as the last available month
      // https://daypicker.dev/docs/navigation#start-and-end-dates
      endMonth={new UTCDate(getYear(new UTCDate()), getMonth(new UTCDate()))}
      locale={enIE}
      classNames={{
        range_start:
          'rounded-full bg-black text-white relative before:content-[""] before:absolute before:inset-0 before:bg-accent before:rounded-none before:left-1/2 before:-z-10',
        range_end:
          'rounded-full bg-black text-white relative before:content-[""] before:absolute before:inset-0 before:bg-accent before:rounded-none before:right-1/2 before:-z-10',
        range_middle: 'bg-accent',
        selected: props.mode === 'single' ? 'rounded-full bg-black text-white' : 'text-sm',
        month: 'mx-auto w-full',
        months: 'flex gap-3',
        month_grid: 'mt-3 border-separate border-spacing-y-[2px]',
        day: 'h-[26px] w-[26px] min-w-[26px] shrink-0 text-center text-black text-xs font-normal leading-6 p-0',
        day_button: 'h-[26px] w-[26px] min-w-[26px] shrink-0 rounded-full focus-visible:outline-primary bg-transparent',
        today: 'text-black font-extrabold',
        outside: 'text-muted-foreground font-medium',
        nav: 'hidden',
        ...classNames,
      }}
      components={{
        Weekdays: CustomWeekdays,
        MonthCaption: CustomCaption,
      }}
      {...props}
    />
  );
};

const days = [
  { label: 'M', value: 'Monday' },
  { label: 'T', value: 'Tuesday' },
  { label: 'W', value: 'Wednesday' },
  { label: 'T', value: 'Thursday' },
  { label: 'F', value: 'Friday' },
  { label: 'S', value: 'Saturday' },
  { label: 'S', value: 'Sunday' },
];

const CustomWeekdays = () => {
  return (
    <Weekdays>
      {days.map((day) => (
        <th
          key={day.value}
          className="h-[26px] w-[26px] min-w-[26px] shrink-0 p-0 text-xs leading-6 font-medium text-muted-foreground"
        >
          {day.label}
        </th>
      ))}
    </Weekdays>
  );
};

const CustomCaption = (props: MonthCaptionProps) => {
  const { nextMonth, previousMonth, goToMonth } = useDayPicker();
  const { calendarMonth, displayIndex } = props;

  const isFirstMonth = displayIndex === 0;
  const isLastMonth = displayIndex === 1;

  const monthLabel = format(calendarMonth.date, 'MMMM yyyy');

  return (
    <Inline justify="between" align="center">
      {isFirstMonth && previousMonth ? (
        <Button
          variant="ghost"
          size="none"
          onClick={() => goToMonth(previousMonth)}
          className="h-[22px] w-[22px]"
          disabled={!previousMonth}
        >
          <div className="h-full w-full rounded-full bg-accent p-[5px] text-black">
            <ChevronLeft className="h-full w-full" />
          </div>
        </Button>
      ) : (
        <div className="h-[22px] w-[22px]" />
      )}

      <span className="text-sm leading-[22px] font-medium text-black">{monthLabel}</span>

      {isLastMonth && nextMonth ? (
        <Button
          variant="ghost"
          size="none"
          onClick={() => goToMonth(nextMonth)}
          className="h-[22px] w-[22px]"
          disabled={!nextMonth}
        >
          <div className="h-full w-full rounded-full bg-accent p-[5px] text-black">
            <ChevronRight className="h-full w-full" />
          </div>
        </Button>
      ) : (
        <div className="h-[22px] w-[22px]" />
      )}
    </Inline>
  );
};
